import { useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  BarChart3,
  Calendar,
  FileSpreadsheet,
  FileText,
  Home,
  Megaphone,
  MoreHorizontal,
  Receipt,
  Settings,
  Users,
  type LucideIcon
} from 'lucide-react';
import { useMe } from '../api/hooks';
import { Sheet } from './Sheet';

const PRIMARY: { to: string; label: string; icon: LucideIcon }[] = [
  { to: '/customers', label: 'Clients', icon: Users },
  { to: '/calendar', label: 'Calendar', icon: Calendar },
  { to: '/receipts', label: 'Receipts', icon: Receipt },
  { to: '/documents', label: 'Documents', icon: FileText }
];

// Routes where the tab bar shows. Deep create/edit/detail routes hide it so their
// StickyActionBar owns the bottom edge and users navigate back via the header.
const TOP_LEVEL = new Set([
  '/dashboard', '/customers', '/calendar', '/receipts', '/estimates',
  '/documents', '/documents/sent', '/documents/templates', '/documents/packages',
  '/documents/validity', '/reports', '/settings', '/settings/records',
  '/settings/calendar-feed', '/settings/document-kinds', '/marketing'
]);

/** Mobile-only bottom tab bar (5 slots: 4 primary + More). Desktop keeps the header nav. */
export function BottomNav() {
  const { pathname } = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);

  if (!TOP_LEVEL.has(pathname)) return null;

  const moreActive = ['/dashboard', '/estimates', '/reports', '/settings', '/marketing'].some(
    (p) => pathname === p || pathname.startsWith(p + '/')
  );

  const tabCls = (active: boolean) =>
    `flex flex-1 flex-col items-center justify-center gap-0.5 py-1.5 text-[11px] ${
      active ? 'text-pine' : 'text-slate-500'
    }`;

  return (
    <>
      <nav
        aria-label="Primary"
        className="fixed inset-x-0 bottom-0 z-30 flex border-t border-slate-200 bg-surface/95 backdrop-blur sm:hidden"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        {PRIMARY.map(({ to, label, icon: Icon }) => (
          <NavLink key={to} to={to} className={({ isActive }) => tabCls(isActive)}>
            <Icon size={20} />
            {label}
          </NavLink>
        ))}
        <button type="button" onClick={() => setMoreOpen(true)} className={tabCls(moreActive)}>
          <MoreHorizontal size={20} />
          More
        </button>
      </nav>
      <MoreDrawer open={moreOpen} onClose={() => setMoreOpen(false)} />
    </>
  );
}

function MoreDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const nav = useNavigate();
  const { data: me } = useMe(true);

  const items: { to: string; label: string; icon: LucideIcon }[] = [
    { to: '/dashboard', label: 'Dashboard', icon: Home },
    { to: '/estimates', label: 'Estimates', icon: FileSpreadsheet },
    { to: '/reports', label: 'Reports', icon: BarChart3 },
    ...(me?.features?.marketingEnabled ? [{ to: '/marketing', label: 'Marketing', icon: Megaphone }] : []),
    { to: '/settings', label: 'Settings', icon: Settings }
  ];

  const go = (to: string) => {
    onClose();
    nav(to);
  };

  return (
    <Sheet open={open} onClose={onClose} title="More">
      <ul className="space-y-1">
        {items.map(({ to, label, icon: Icon }) => (
          <li key={to}>
            <button
              type="button"
              onClick={() => go(to)}
              className="flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left text-sm text-ink hover:bg-slate-50"
            >
              <Icon size={18} className="text-slate-500" />
              {label}
            </button>
          </li>
        ))}
      </ul>
    </Sheet>
  );
}
